/**
 * Bike Information Page
 * =====================
 * Main information page for a motorcycle with specs and details.
 */

import { useParams, Link } from 'react-router-dom'
import { useMotorcycle } from '@/hooks/useMotorcycles'
import BikePageHeader from './BikePageHeader'
import {
  Info,
  FileText,
  Ruler,
  Zap,
  Palette,
  Wallet,
  ExternalLink,
  ChevronRight,
  Hash,
  Cog,
  Wind,
} from 'lucide-react'
import { BIKE_STATUS_LABELS, BIKE_STATUS_COLORS, BIKE_TYPE_LABELS } from '@/types'

// =============================================================================
// Static Data for XT350
// =============================================================================

const XT350_INFO = {
  identity: {
    vin: '55V-030739',
    registration: 'HU847',
    firstRegistered: '15.05.1986',
    country: 'Iceland',
    odometer: '~18,733 km',
    previousOwners: 22,
    category: 'Fornökutæki (Vintage vehicle)',
  },
  engine: {
    type: 'Air-cooled, 4-stroke, DOHC single cylinder',
    valves: '4 (2 intake, 2 exhaust)',
    induction: 'Y.D.I.S. (Yamaha Dual Intake System)',
    displacement: '346 cc',
    boreStroke: '86.0 mm x 59.6 mm',
    compression: '9.0:1',
    carburetor: 'Teikei Y24PV (dual)',
    power: '31.5 HP (22.8 kW) @ 7,500 RPM',
    torque: '29.5 Nm (21.7 lb-ft) @ 7,000 RPM',
    ignition: 'CDI',
    starting: 'Kick',
    lubrication: 'Wet sump',
    oilCapacity: '1.3L (change), 1.6L (total)',
    recommendedOil: 'SAE 20W40 SE',
    transmission: '6-speed manual',
    finalDrive: 'Chain (428)',
  },
  chassis: {
    frame: 'Semi-double cradle, steel',
    frontSuspension: 'Telescopic fork, 254mm (10") travel',
    rearSuspension: 'Monocross single shock, 220mm (8.6") travel',
    frontBrake: '220mm hydraulic disc, single piston',
    rearBrake: 'Drum',
    frontTire: '3.00-21',
    rearTire: '4.60-18 (OEM) / 110/80-18 (modern)',
    frontWheel: '21"',
    rearWheel: '18"',
  },
  dimensions: {
    length: '2,210 mm (87.0 in)',
    width: '865 mm (34.0 in)',
    height: '1,210 mm (47.6 in)',
    wheelbase: '1,420 mm (55.9 in)',
    seatHeight: '855 mm (33.7 in)',
    groundClearance: '275 mm (10.8 in)',
    dryWeight: '120 kg (265 lbs)',
    wetWeight: '133 kg (293 lbs)',
    fuelCapacity: '12 L (3.2 US gal)',
  },
  performance: {
    topSpeed: '~145 km/h (90 mph)',
    fuelConsumption: '~4.5 L/100km (52 mpg)',
  },
  colors: [
    { part: 'Frame', color: 'Traffic Red (powder coat)', code: 'RAL 3020' },
    { part: 'Tank/Fenders', color: 'Fire Red', code: 'Yamaha 00FH / ColorRite 5280' },
    { part: 'Plastics', color: 'White', code: 'Yamaha 0036 / ColorRite 5528' },
  ],
  investment: {
    purchasePrice: '250,000 ISK',
    partsLabor: '~1,500,000 ISK',
    total: '~1,750,000 ISK',
    hoursInvested: '~300 hours',
  },
  documentationRepo: 'haugur/Yamaha-XT350',
}

// =============================================================================
// Helper Components
// =============================================================================

interface SpecCardProps {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
  className?: string
}

function SpecCard({ icon, title, children, className = '' }: SpecCardProps) {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 ${className}`}>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        {icon}
        {title}
      </h2>
      {children}
    </div>
  )
}

interface SpecRowProps {
  label: string
  value: string | number | null | undefined
}

function SpecRow({ label, value }: SpecRowProps) {
  if (!value) return null
  return (
    <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <span className="text-gray-500 dark:text-gray-400">{label}</span>
      <span className="text-gray-900 dark:text-white font-medium text-right">{value}</span>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export default function BikeInformation() {
  const { bikeId } = useParams<{ bikeId: string }>()
  const { data: bike, isLoading, error } = useMotorcycle(bikeId || '')

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load motorcycle</p>
      </div>
    )
  }

  // Check if this is the XT350 (has detailed info)
  const isXT350 = bike?.motorcycle_id === 'MOTO-0001' || bike?.make?.toLowerCase() === 'yamaha'
  const info = isXT350 ? XT350_INFO : null

  return (
    <div>
      <BikePageHeader bike={bike} isLoading={isLoading} />

      {isLoading ? (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-2" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
          </div>
        </div>
      ) : info ? (
        // XT350 - Full detailed view
        <div className="space-y-6">
          {/* Hero with primary image and quick stats */}
          <div className="bg-gradient-to-br from-red-600 to-red-800 rounded-xl p-6 text-white">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Image */}
              {bike?.image_url && (
                <div className="lg:w-1/3 flex-shrink-0">
                  <img
                    src={bike.image_url}
                    alt={bike.name}
                    className="w-full h-48 lg:h-64 object-cover rounded-lg shadow-lg"
                  />
                </div>
              )}
              {/* Quick info */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${BIKE_STATUS_COLORS[bike?.status || 'running']}`}
                  >
                    {BIKE_STATUS_LABELS[bike?.status || 'running']}
                  </span>
                  <span className="text-red-200">{BIKE_TYPE_LABELS[bike?.bike_type || 'dual_sport']}</span>
                </div>
                <h1 className="text-3xl font-bold mb-2">{bike?.name}</h1>
                <p className="text-red-100 mb-4">
                  {bike?.year} {bike?.make} {bike?.model}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2">
                    <div className="text-red-200 text-xs">Engine</div>
                    <div className="font-semibold">{info.engine.displacement}</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2">
                    <div className="text-red-200 text-xs">Power</div>
                    <div className="font-semibold">{info.engine.power.split('@')[0].trim()}</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2">
                    <div className="text-red-200 text-xs">Weight</div>
                    <div className="font-semibold">{info.dimensions.dryWeight.split('(')[0].trim()}</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2">
                    <div className="text-red-200 text-xs">Top Speed</div>
                    <div className="font-semibold">{info.performance.topSpeed.split('(')[0].trim()}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Vehicle Identity */}
          <SpecCard icon={<Hash size={20} className="text-primary-600 dark:text-primary-400" />} title="Vehicle Identity">
            <div className="grid md:grid-cols-2 gap-x-8">
              <SpecRow label="VIN / Frame" value={info.identity.vin} />
              <SpecRow label="Registration" value={info.identity.registration} />
              <SpecRow label="First Registered" value={info.identity.firstRegistered} />
              <SpecRow label="Country" value={info.identity.country} />
              <SpecRow label="Odometer" value={info.identity.odometer} />
              <SpecRow label="Previous Owners" value={info.identity.previousOwners} />
              <SpecRow label="Category" value={info.identity.category} />
            </div>
          </SpecCard>

          {/* Engine Specifications */}
          <SpecCard icon={<Cog size={20} className="text-primary-600 dark:text-primary-400" />} title="Engine">
            <div className="grid md:grid-cols-2 gap-x-8">
              <SpecRow label="Type" value={info.engine.type} />
              <SpecRow label="Valves" value={info.engine.valves} />
              <SpecRow label="Displacement" value={info.engine.displacement} />
              <SpecRow label="Bore x Stroke" value={info.engine.boreStroke} />
              <SpecRow label="Compression" value={info.engine.compression} />
              <SpecRow label="Induction" value={info.engine.induction} />
              <SpecRow label="Carburetor" value={info.engine.carburetor} />
              <SpecRow label="Power" value={info.engine.power} />
              <SpecRow label="Torque" value={info.engine.torque} />
              <SpecRow label="Ignition" value={info.engine.ignition} />
              <SpecRow label="Starting" value={info.engine.starting} />
              <SpecRow label="Lubrication" value={info.engine.lubrication} />
              <SpecRow label="Oil Capacity" value={info.engine.oilCapacity} />
              <SpecRow label="Recommended Oil" value={info.engine.recommendedOil} />
              <SpecRow label="Transmission" value={info.engine.transmission} />
              <SpecRow label="Final Drive" value={info.engine.finalDrive} />
            </div>
          </SpecCard>

          {/* Chassis & Suspension */}
          <SpecCard icon={<Wind size={20} className="text-primary-600 dark:text-primary-400" />} title="Chassis & Suspension">
            <div className="grid md:grid-cols-2 gap-x-8">
              <SpecRow label="Frame" value={info.chassis.frame} />
              <SpecRow label="Front Suspension" value={info.chassis.frontSuspension} />
              <SpecRow label="Rear Suspension" value={info.chassis.rearSuspension} />
              <SpecRow label="Front Brake" value={info.chassis.frontBrake} />
              <SpecRow label="Rear Brake" value={info.chassis.rearBrake} />
              <SpecRow label="Front Tire" value={info.chassis.frontTire} />
              <SpecRow label="Rear Tire" value={info.chassis.rearTire} />
              <SpecRow label="Front Wheel" value={info.chassis.frontWheel} />
              <SpecRow label="Rear Wheel" value={info.chassis.rearWheel} />
            </div>
          </SpecCard>

          {/* Dimensions & Weight */}
          <SpecCard icon={<Ruler size={20} className="text-primary-600 dark:text-primary-400" />} title="Dimensions & Weight">
            <div className="grid md:grid-cols-2 gap-x-8">
              <SpecRow label="Length" value={info.dimensions.length} />
              <SpecRow label="Width" value={info.dimensions.width} />
              <SpecRow label="Height" value={info.dimensions.height} />
              <SpecRow label="Wheelbase" value={info.dimensions.wheelbase} />
              <SpecRow label="Seat Height" value={info.dimensions.seatHeight} />
              <SpecRow label="Ground Clearance" value={info.dimensions.groundClearance} />
              <SpecRow label="Dry Weight" value={info.dimensions.dryWeight} />
              <SpecRow label="Wet Weight" value={info.dimensions.wetWeight} />
              <SpecRow label="Fuel Capacity" value={info.dimensions.fuelCapacity} />
            </div>
          </SpecCard>

          {/* Performance */}
          <SpecCard icon={<Zap size={20} className="text-primary-600 dark:text-primary-400" />} title="Performance">
            <div className="grid md:grid-cols-2 gap-x-8">
              <SpecRow label="Top Speed" value={info.performance.topSpeed} />
              <SpecRow label="Fuel Consumption" value={info.performance.fuelConsumption} />
            </div>
          </SpecCard>

          {/* Colors */}
          <SpecCard icon={<Palette size={20} className="text-primary-600 dark:text-primary-400" />} title="Paint Colors">
            <div className="space-y-3">
              {info.colors.map((c, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                  <span className="text-gray-500 dark:text-gray-400">{c.part}</span>
                  <div className="text-right">
                    <div className="text-gray-900 dark:text-white font-medium">{c.color}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{c.code}</div>
                  </div>
                </div>
              ))}
            </div>
          </SpecCard>

          {/* Project Investment */}
          <SpecCard icon={<Wallet size={20} className="text-primary-600 dark:text-primary-400" />} title="Project Investment">
            <div className="grid md:grid-cols-2 gap-x-8">
              <SpecRow label="Purchase Price" value={info.investment.purchasePrice} />
              <SpecRow label="Parts & Labor" value={info.investment.partsLabor} />
              <SpecRow label="Total Investment" value={info.investment.total} />
              <SpecRow label="Hours Invested" value={info.investment.hoursInvested} />
            </div>
          </SpecCard>

          {/* Documentation */}
          {info.documentationRepo && (
            <SpecCard icon={<FileText size={20} className="text-primary-600 dark:text-primary-400" />} title="Documentation">
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Full project documentation including restoration log, parts lists, manuals, and photos.
              </p>
              <a
                href={`https://git.asgeir.is/${info.documentationRepo}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                <ExternalLink size={16} />
                View on Forgejo
              </a>
            </SpecCard>
          )}
        </div>
      ) : (
        // Other bikes - Basic info from database
        <div className="space-y-6">
          {/* Basic Info Card */}
          <SpecCard icon={<Info size={20} className="text-primary-600 dark:text-primary-400" />} title="Basic Information">
            <div className="grid md:grid-cols-2 gap-x-8">
              <SpecRow label="ID" value={bike?.motorcycle_id} />
              <SpecRow label="Name" value={bike?.name} />
              <SpecRow label="Make" value={bike?.make} />
              <SpecRow label="Model" value={bike?.model} />
              <SpecRow label="Year" value={bike?.year} />
              <SpecRow label="VIN" value={bike?.vin} />
              <SpecRow label="Type" value={bike?.bike_type ? BIKE_TYPE_LABELS[bike.bike_type] : null} />
              <SpecRow label="Status" value={bike?.status ? BIKE_STATUS_LABELS[bike.status] : null} />
              <SpecRow label="Engine" value={bike?.engine_cc ? `${bike.engine_cc}cc` : null} />
              <SpecRow label="Engine Type" value={bike?.engine_type} />
              <SpecRow label="Color" value={bike?.color} />
              <SpecRow label="Purchase Date" value={bike?.purchase_date} />
              <SpecRow label="Purchase Price" value={bike?.purchase_price} />
            </div>
          </SpecCard>

          {/* Notes */}
          {bike?.notes && (
            <SpecCard icon={<FileText size={20} className="text-primary-600 dark:text-primary-400" />} title="Notes">
              <p className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{bike.notes}</p>
            </SpecCard>
          )}

          {/* Documentation Link */}
          {bike?.documentation_repo && (
            <SpecCard icon={<ExternalLink size={20} className="text-primary-600 dark:text-primary-400" />} title="Documentation">
              <a
                href={`https://git.asgeir.is/${bike.documentation_repo}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                <ExternalLink size={16} />
                View on Forgejo
              </a>
            </SpecCard>
          )}

          {/* Placeholder for missing detailed specs */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              Detailed specifications for this motorcycle have not yet been documented.
            </p>
            <Link
              to={`/motorcycles/${bike?.motorcycle_id}/edit`}
              className="inline-flex items-center gap-1 mt-2 text-primary-600 dark:text-primary-400 hover:underline"
            >
              Edit motorcycle details
              <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
